import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const NoticiaForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        titulo: '',
        contenido: '',
        tipo: 'info',
        prioridad: 'media',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: '',
        activo: true,
        mostrar_en_dashboard: true,
        icono: 'fas fa-info-circle'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        if (isEdit) {
            fetchNoticia();
        }
    }, [history, id, isEdit]);

    const fetchNoticia = async () => {
        try {
            const response = await axios.get(`/superadmin/noticias/${id}`);
            const noticia = response.data;
            
            // Convertir fechas de timestamp a formato yyyy-MM-dd
            const formatearFecha = (fecha) => {
                if (!fecha) return '';
                return fecha.split(' ')[0]; // Toma solo la parte de la fecha (yyyy-MM-dd)
            };
            
            setFormData({
                titulo: noticia.titulo,
                contenido: noticia.contenido,
                tipo: noticia.tipo,
                prioridad: noticia.prioridad || 'media',
                fecha_inicio: formatearFecha(noticia.fecha_inicio),
                fecha_fin: formatearFecha(noticia.fecha_fin),
                activo: Boolean(noticia.activo),
                mostrar_en_dashboard: Boolean(noticia.mostrar_en_dashboard ?? true),
                icono: noticia.icono || 'fas fa-info-circle'
            });
        } catch (error) {
            console.error('Error fetching noticia:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: 'Error al cargar la noticia'
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.titulo.trim()) {
            newErrors.titulo = 'El título es requerido';
        }

        if (!formData.contenido.trim()) {
            newErrors.contenido = 'El contenido es requerido';
        }

        if (!formData.fecha_inicio) {
            newErrors.fecha_inicio = 'La fecha de inicio es requerida';
        }

        if (formData.fecha_fin && formData.fecha_fin < formData.fecha_inicio) {
            newErrors.fecha_fin = 'La fecha fin debe ser posterior a la fecha de inicio';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            if (isEdit) {
                await axios.put(`/superadmin/noticias/${id}`, formData);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Noticia actualizada exitosamente'
                });
            } else {
                await axios.post('/superadmin/noticias', formData);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Noticia creada exitosamente'
                });
            }

            setTimeout(() => {
                history.push('/superadmin/noticias');
            }, 1500);
        } catch (error) {
            console.error('Error saving noticia:', error);
            const message = error.response?.data?.message || 'Error al guardar la noticia';
            setAlert({
                show: true,
                type: 'danger',
                message: message
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-bullhorn"></i> {isEdit ? 'Editar Noticia' : 'Nueva Noticia'}
                </h4>
                <button
                    className="btn btn-secondary"
                    onClick={() => history.push('/superadmin/noticias')}
                >
                    <i className="fas fa-arrow-left"></i> Volver
                </button>
            </div>

            {/* Alertas */}
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className="card">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label>Título <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.titulo ? 'is-invalid' : ''}`}
                                        name="titulo"
                                        value={formData.titulo}
                                        onChange={handleChange}
                                        placeholder="Ej: Mantenimiento programado"
                                    />
                                    {errors.titulo && <div className="invalid-feedback">{errors.titulo}</div>}
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Tipo <span className="text-danger">*</span></label>
                                    <select
                                        className="form-control"
                                        name="tipo"
                                        value={formData.tipo}
                                        onChange={handleChange}
                                    >
                                        <option value="info">Información (Azul)</option>
                                        <option value="secondary">Neutral (Gris)</option>
                                        <option value="light">Claro (Blanco)</option>
                                        <option value="dark">Oscuro (Negro)</option>
                                        <option value="warning">Advertencia (Amarillo)</option>
                                        <option value="danger">Problema (Rojo)</option>
                                        <option value="success">Éxito (Verde)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Prioridad <span className="text-danger">*</span></label>
                                    <select
                                        className="form-control"
                                        name="prioridad"
                                        value={formData.prioridad}
                                        onChange={handleChange}
                                    >
                                        <option value="baja">Baja</option>
                                        <option value="media">Media</option>
                                        <option value="alta">Alta</option>
                                        <option value="critica">Crítica</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Contenido <span className="text-danger">*</span></label>
                            <textarea
                                className={`form-control ${errors.contenido ? 'is-invalid' : ''}`}
                                name="contenido"
                                value={formData.contenido}
                                onChange={handleChange}
                                rows="5"
                                placeholder="Describe la noticia o anuncio..."
                            />
                            {errors.contenido && <div className="invalid-feedback">{errors.contenido}</div>}
                        </div>

                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>Fecha Inicio <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.fecha_inicio ? 'is-invalid' : ''}`}
                                        name="fecha_inicio"
                                        value={formData.fecha_inicio}
                                        onChange={handleChange}
                                    />
                                    {errors.fecha_inicio && <div className="invalid-feedback">{errors.fecha_inicio}</div>}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-group">
                                    <label>Fecha Fin (Opcional)</label>
                                    <input
                                        type="date"
                                        className={`form-control ${errors.fecha_fin ? 'is-invalid' : ''}`}
                                        name="fecha_fin"
                                        value={formData.fecha_fin}
                                        onChange={handleChange}
                                    />
                                    {errors.fecha_fin && <div className="invalid-feedback">{errors.fecha_fin}</div>}
                                    <small className="form-text text-muted">
                                        Si no se especifica, la noticia se mostrará indefinidamente
                                    </small>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-group">
                                  
                                    <div className="mt-2">
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="activo"
                                                name="activo"
                                                checked={formData.activo}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="activo">
                                                {formData.activo ? 'Activa' : 'Inactiva'}
                                            </label>
                                        </div>
                                       
                                    </div>
                                </div>
                           

 
 <div className="custom-control custom-switch mt-4">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="mostrar_en_dashboard"
                                                name="mostrar_en_dashboard"
                                                checked={formData.mostrar_en_dashboard}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="mostrar_en_dashboard">
                                                Mostrar en Dashboard
                                            </label>
                                        </div>



 </div>


                        </div>

                        <div className="form-group mt-4">
                            <button
                                type="submit"
                                className="btn btn-superadmin"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i> Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save"></i> {isEdit ? 'Actualizar' : 'Crear'} Noticia
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary ml-2"
                                onClick={() => history.push('/superadmin/noticias')}
                            >
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </SuperadminLayout>
    );
};

export default NoticiaForm;
